import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ShoppingBag, User } from 'lucide-react';
import { useImages } from '../hooks/useImages';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';

const LOGO_DEFAULT = 'https://customer-assets.emergentagent.com/job_soil-to-soul-2/artifacts/i9fai1ns_WhatsApp_Image_2026-03-16_at_10.39.52_AM-removebg-preview.png';

const ecosystemLinks = [
  { name: 'Organic Hues', path: '/organic-hues' },
  { name: 'Green Horizon', path: '/green-horizon' },
  { name: 'EcoRoute', path: '/ecoroute' },
  { name: 'WHeroes', path: '/wheros' },
  { name: 'Rise & Rolls', path: '/rise-and-rolls' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [ecoOpen, setEcoOpen] = useState(false);
  const [mobileEcoOpen, setMobileEcoOpen] = useState(false);
  const ecoRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const siteImages = useImages();
  const logo = siteImages['site-logo'] || LOGO_DEFAULT;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ecoRef.current && !ecoRef.current.contains(e.target)) setEcoOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setIsMobileMenuOpen(false); setMobileEcoOpen(false); }, [location.pathname]);

  const isEcoActive = ecosystemLinks.some(l => location.pathname === l.path);
  const showLight = isHome && !isScrolled;
  const { totalItems, setIsCartOpen } = useCart();
  const { user } = useAuth();

  const linkClass = (active) =>
    `px-4 py-2 text-sm font-medium transition-all duration-300 rounded-md ${
      active
        ? showLight ? 'text-white bg-white/15 backdrop-blur-sm' : 'text-[#5C7A3A] bg-[#5C7A3A]/10'
        : showLight ? 'text-white/85 hover:text-white hover:bg-white/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md shadow-[#D4A574]/5'
          : isHome ? 'bg-transparent' : 'bg-white/95 backdrop-blur-md shadow-md shadow-[#D4A574]/5'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          <Link to="/" className="flex items-center space-x-3 mt-2">
            <img
              src={logo}
              alt="Nistala's Group"
              className="w-auto h-36 transition-all duration-300"
              style={showLight ? { filter: 'brightness(10) drop-shadow(0 0 8px rgba(255,255,255,0.4))' } : {}}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link to="/" data-testid="nav-home" className={linkClass(location.pathname === '/')}>Home</Link>
            <Link to="/products" data-testid="nav-products" className={linkClass(location.pathname === '/products')}>Products</Link>
            <Link to="/about" data-testid="nav-about" className={linkClass(location.pathname === '/about')}>About Us</Link>

            {/* Ecosystem Dropdown */}
            <div ref={ecoRef} className="relative" onMouseEnter={() => setEcoOpen(true)} onMouseLeave={() => setEcoOpen(false)}>
              <button
                onClick={() => setEcoOpen(!ecoOpen)}
                data-testid="nav-ecosystem"
                className={`${linkClass(isEcoActive)} inline-flex items-center gap-1 cursor-pointer`}
              >
                Ecosystem
                <ChevronDown size={14} className={`transition-transform duration-200 ${ecoOpen ? 'rotate-180' : ''}`} />
              </button>
              {ecoOpen && (
                <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-xl shadow-[#D4A574]/10 border border-[#E8DFD3] py-2 z-50" data-testid="ecosystem-dropdown">
                  {ecosystemLinks.map(link => (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`nav-eco-${link.path.replace('/', '')}`}
                      className={`block px-4 py-2.5 text-sm transition-colors ${
                        location.pathname === link.path
                          ? 'text-[#5C7A3A] bg-[#5C7A3A]/8 font-semibold'
                          : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#FDF8F0]'
                      }`}
                      onClick={() => setEcoOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/blogs" data-testid="nav-blogs" className={linkClass(location.pathname === '/blogs')}>Blogs</Link>
            <Link to="/membership" data-testid="nav-membership" className={linkClass(location.pathname === '/membership')}>Membership</Link>
            <Link to="/catering" data-testid="nav-catering" className={linkClass(location.pathname === '/catering')}>Catering</Link>
            <Link to="/careers" data-testid="nav-careers" className={linkClass(location.pathname === '/careers')}>Careers</Link>
            <Link to="/contact" data-testid="nav-contact" className={linkClass(location.pathname === '/contact')}>Contact Us</Link>

            {/* Cart Button */}
            <button
              onClick={() => setIsCartOpen(true)}
              data-testid="nav-cart-btn"
              className={`relative ml-2 p-2.5 rounded-lg transition-all duration-300 cursor-pointer ${
                showLight ? 'text-white/85 hover:text-white hover:bg-white/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'
              }`}
            >
              <ShoppingBag size={20} />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#C4844A] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>

            {/* User Account */}
            <Link
              to={user ? '/account' : '/login'}
              data-testid="nav-account-btn"
              className={`relative p-2.5 rounded-lg transition-all duration-300 cursor-pointer ${
                showLight ? 'text-white/85 hover:text-white hover:bg-white/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'
              }`}
            >
              <User size={20} />
              {user && (
                <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-[#5C7A3A] rounded-full border-2 border-white"></span>
              )}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={() => setIsCartOpen(true)}
              data-testid="mobile-cart-btn"
              className={`relative p-2 rounded-md transition-colors cursor-pointer ${showLight ? 'text-white hover:bg-white/10' : 'text-[#2C1810] hover:bg-[#5C7A3A]/10'}`}
            >
              <ShoppingBag size={22} />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#C4844A] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>
            <button
              className={`p-2 rounded-md transition-colors cursor-pointer ${showLight ? 'text-white hover:bg-white/10' : 'text-[#2C1810] hover:bg-[#5C7A3A]/10'}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-[#E8DFD3] bg-white/95 backdrop-blur-md rounded-b-xl">
            <Link to="/" className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === '/' ? 'text-[#5C7A3A] bg-[#5C7A3A]/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'}`}>Home</Link>
            <Link to="/products" className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === '/products' ? 'text-[#5C7A3A] bg-[#5C7A3A]/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'}`}>Products</Link>
            <Link to="/about" className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === '/about' ? 'text-[#5C7A3A] bg-[#5C7A3A]/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'}`}>About Us</Link>

            {/* Mobile Ecosystem Accordion */}
            <button
              onClick={() => setMobileEcoOpen(!mobileEcoOpen)}
              className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors cursor-pointer ${isEcoActive ? 'text-[#5C7A3A] bg-[#5C7A3A]/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'}`}
            >
              Ecosystem
              <ChevronDown size={14} className={`transition-transform duration-200 ${mobileEcoOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileEcoOpen && (
              <div className="bg-[#FDF8F0]/60 border-l-2 border-[#D4A574]/30 ml-4">
                {ecosystemLinks.map(link => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-2.5 text-sm transition-colors ${
                      location.pathname === link.path ? 'text-[#5C7A3A] font-semibold' : 'text-[#7a6555] hover:text-[#5C7A3A]'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            )}

            <Link to="/blogs" className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === '/blogs' ? 'text-[#5C7A3A] bg-[#5C7A3A]/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'}`}>Blogs</Link>
            <Link to="/membership" className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === '/membership' ? 'text-[#5C7A3A] bg-[#5C7A3A]/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'}`}>Membership</Link>
            <Link to="/catering" className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === '/catering' ? 'text-[#5C7A3A] bg-[#5C7A3A]/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'}`}>Catering</Link>
            <Link to="/careers" className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === '/careers' ? 'text-[#5C7A3A] bg-[#5C7A3A]/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'}`}>Careers</Link>
            <Link to="/contact" className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === '/contact' ? 'text-[#5C7A3A] bg-[#5C7A3A]/10' : 'text-[#2C1810] hover:text-[#5C7A3A] hover:bg-[#5C7A3A]/5'}`}>Contact Us</Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
